import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductionFinishedInventory() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [masterArticles, setMasterArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    article_id: '',
    article_name: '',
    production_status: 'ready',
    quantity_planned: 0,
    quantity_produced: 0,
    notes: ''
  });

  useEffect(() => {
    fetchInventory();
    fetchMasterArticles();
  }, []);

  const fetchMasterArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data?entity_type=article`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMasterArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles');
    }
  };

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/production-finished-inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to fetch finished inventory');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        quantity_planned: parseFloat(formData.quantity_planned),
        quantity_produced: parseFloat(formData.quantity_produced)
      };
      if (editItem) {
        payload.id = editItem.id;
        await axios.put(`${API}/production-finished-inventory/${editItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/production-finished-inventory`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditItem(null);
      setFormData({ article_id: '', article_name: '', production_status: 'ready', quantity_planned: 0, quantity_produced: 0, notes: '' });
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/production-finished-inventory/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchInventory();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Finished Goods Inventory</h1>
              <p className="text-sm text-gray-600">Completed production items</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-end mb-6">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditItem(null); setFormData({ article_id: '', article_name: '', production_status: 'ready', quantity_planned: 0, quantity_produced: 0, notes: '' }); }} data-testid="add-finished-inventory-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="finished-inventory-dialog">
              <DialogHeader>
                <DialogTitle>{editItem ? 'Edit Item' : 'Add Finished Inventory'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Article</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.article_id}
                    onChange={(e) => {
                      const article = masterArticles.find(a => a.id === e.target.value);
                      setFormData({ ...formData, article_id: e.target.value, article_name: article?.name || '' });
                    }} required data-testid="article-select">
                    <option value="">Select Article</option>
                    {masterArticles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Production Status</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.production_status} onChange={(e) => setFormData({ ...formData, production_status: e.target.value })} data-testid="status-select">
                    <option value="ready">Ready</option>
                    <option value="in_production">In Production</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quantity Planned</Label>
                    <Input type="number" value={formData.quantity_planned} onChange={(e) => setFormData({ ...formData, quantity_planned: e.target.value })} required data-testid="quantity-planned-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity Produced</Label>
                    <Input type="number" value={formData.quantity_produced} onChange={(e) => setFormData({ ...formData, quantity_produced: e.target.value })} data-testid="quantity-produced-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <textarea className="w-full px-3 py-2 border rounded-md" rows="3" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-finished-inventory">{editItem ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Planned</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produced</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inventory.map((item) => {
                const progress = item.quantity_planned > 0 ? (item.quantity_produced / item.quantity_planned * 100).toFixed(0) : 0;
                return (
                  <tr key={item.id} data-testid={`finished-inventory-row-${item.id}`}>
                    <td className="px-6 py-4">{item.article_name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.production_status === 'completed' ? 'bg-green-100 text-green-800' :
                        item.production_status === 'in_production' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{item.production_status}</span>
                    </td>
                    <td className="px-6 py-4">{item.quantity_planned}</td>
                    <td className="px-6 py-4">{item.quantity_produced}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500" style={{ width: `${progress}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-600">{progress}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setFormData(item); setOpen(true); }} data-testid={`edit-finished-inventory-${item.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`delete-finished-inventory-${item.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {inventory.length === 0 && <div className="text-center py-12" data-testid="no-finished-inventory"><p className="text-gray-500">No finished inventory found</p></div>}
      </main>
    </div>
  );
}
